import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Heart, MessageCircle, Eye, Pin } from "lucide-react";
import { api } from "../lib/api";

/** Compact "TOP CREATIONS" strip for the News page.
 *  Fetches the top 3 community creations and links each to /community#creations. */
export default function TopCreations() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/community-creations?sort=top")
      .then(r => { if (!cancelled) setRows((r.data?.top || []).slice(0, 3)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (rows.length === 0) return null;

  return (
    <section className="mb-6" data-testid="news-top-creations">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={12} className="sat-cyan" />
        <span className="sat-label sat-cyan">TOP CREATIONS · FROM THE COMMUNITY</span>
        <Link to="/community#creations" className="ml-auto sat-label hover:sat-cyan"
              data-testid="news-top-creations-more">→ SEE ALL</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rows.map(c => {
          const reactionTotal = Object.values(c.reactions || {}).reduce((a, b) => a + b, 0);
          const hero = (c.attachments || []).find(a => (a.kind === "image") || a.content_type?.startsWith?.("image/"));
          return (
            <Link
              key={c.id}
              to="/community#creations"
              data-testid={`news-top-creation-${c.id}`}
              className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-cyan)] transition-colors p-3 flex flex-col group"
            >
              {hero && (
                <div className="mb-2 aspect-video overflow-hidden border border-[color:var(--sat-border)]">
                  <img src={hero.url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </div>
              )}
              <div className="sat-label sat-cyan flex items-center gap-2">
                <Sparkles size={10} /> @{c.author_callsign}
                {c.pinned_at && (
                  <span className="border border-[color:var(--sat-cyan)] text-[color:var(--sat-cyan)] text-[9px] px-1 py-0.5 inline-flex items-center gap-0.5">
                    <Pin size={7} /> PINNED
                  </span>
                )}
              </div>
              <div className="font-display font-bold text-base mt-1 leading-snug line-clamp-2 group-hover:sat-cyan transition-colors">
                {c.title}
              </div>
              {c.tags && c.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {c.tags.slice(0, 3).map(t => (
                    <span key={t} className="border border-[color:var(--sat-border)] text-[color:var(--sat-text-dim)] font-mono text-[9px] px-1 py-0.5">#{t}</span>
                  ))}
                </div>
              )}
              <div className="sat-label text-[color:var(--sat-text-dim)] mt-auto pt-2 flex items-center gap-3">
                <span className="flex items-center gap-1"><Eye size={9} /> {c.view_count}</span>
                <span className="flex items-center gap-1"><MessageCircle size={9} /> {c.comment_count}</span>
                <span className="flex items-center gap-1"><Heart size={9} /> {reactionTotal}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
